import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  FaExclamation,
  FaCheck,
  FaHourglassHalf,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";

const ConversationProcessingJobDetailsModal = ({ jobData, onClose, jobId }) => {
  const [expandParameters, setExpandParameters] = useState(false);
  const [expandSteps, setExpandSteps] = useState(false);

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const jobName = jobData.job_name || "Unnamed Job";

  const latestStatus = jobData.run_history[0].status;

  const hasError =
    (latestStatus === "FATAL_ERROR" || latestStatus === "NON_FATAL_ERROR") &&
    jobData.run_history[0]?.errors;

  const progressSteps = jobData.run_history.find(
    (item) => item.status === "IN_PROGRESS",
  )?.steps;

  const getStatusIcon = (status) => {
    const response =
      jobData.run_history[0]?.result?.comm_service_responses | null;

    switch (status) {
      case "FATAL_ERROR":
        return <FaExclamation className="text-red-500" title="Error" />;
      case "NON_FATAL_ERROR":
        return <FaTimes className="text-red-500" title="Error" />;
      case "CANCELLED":
        return <FaTimes className="text-gray-500" title="Cancelled" />;
      case "COMPLETED":
        if (!response)
          return <FaCheck className="text-gray-500" title="Completed" />;
        return <FaCheck className="text-green-500" title="Completed" />;
      case "IN_PROGRESS":
        return (
          <FaSpinner
            className="text-yellow-500 animate-spin"
            title="In Progress"
          />
        );
      case "STARTED":
        return <FaHourglassHalf className="text-yellow-500" title="Started" />;
      case "SUBMITTED":
        return (
          <FaHourglassHalf className="text-yellow-500" title="Submitted" />
        );
      default:
        return (
          <FaHourglassHalf className="text-gray-500" title="Unknown Status" />
        );
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="card-glass rounded-lg p-8 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={handleContentClick}
      >
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl text-white font-semibold">{jobName}</h2>
            <p className="text-gray-400 text-sm mt-1">Job ID: {jobId}</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-2xl flex items-center">
              <span className="mr-2 text-sm text-white font-medium">
                {latestStatus.replace("_", " ")}
              </span>
              {getStatusIcon(latestStatus)}
            </div>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto dark-slim-scrollbar">
          {hasError && (
            <div className="mb-6">
              <h3 className="text-red-400 mb-2 font-medium">Error</h3>
              <div className="p-4 bg-red-900 bg-opacity-20 rounded border border-red-700">
                <p className="text-red-400 font-medium">
                  {jobData.run_history[0].errors?.error_type || "Error"}
                </p>
                <p className="text-gray-300 mt-2">
                  {jobData.run_history[0].errors?.error_message}
                </p>
              </div>
            </div>
          )}

          {!hasError && jobData.run_history[0]?.result && (
            <div className="mb-6">
              <h3 className="text-gray-300 mb-2 font-medium">Results</h3>
              <div className="p-4 bg-gray-800 bg-opacity-30 rounded">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                  {JSON.stringify(jobData.run_history[0].result, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <div className="mb-6">
            <button
              className="text-gray-300 hover:text-white flex items-center font-medium mb-2"
              onClick={() => setExpandSteps(!expandSteps)}
            >
              <span>Job Steps</span>
              <span className="ml-2">{expandSteps ? "▲" : "▼"}</span>
            </button>

            {expandSteps && (
              <div className="p-4 bg-gray-800 bg-opacity-30 rounded">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                  {JSON.stringify(progressSteps, null, 2)}
                </pre>
              </div>
            )}
          </div>

          <div className="mb-6">
            <button
              className="text-gray-300 hover:text-white flex items-center font-medium mb-2"
              onClick={() => setExpandParameters(!expandParameters)}
            >
              <span>Job Parameters</span>
              <span className="ml-2">{expandParameters ? "▲" : "▼"}</span>
            </button>

            {expandParameters && (
              <div className="p-4 bg-gray-800 bg-opacity-30 rounded">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                  {JSON.stringify(jobData.job_parameters, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConversationProcessingJobDetailsModal;
